import { useParams, Link, Navigate } from "react-router-dom";
import { CheckCircle2, ArrowLeft, MapPin } from "lucide-react";
import { SITE } from "../lib/site";
import { getZone, ZONES, SITE_URL } from "../lib/zones";
import { Reveal, MaskedLines } from "../components/Reveal";
import { ContactMenu } from "../components/ContactMenu";
import { Marquee } from "../components/Marquee";
import { useSeo } from "../hooks/useSeo";

const benefits = [
  "Ritiro gratuito a domicilio",
  "Radiazione al PRA inclusa",
  "Certificato di demolizione",
  "Auto anche senza documenti",
  "Veicoli incidentati o fermi",
  "Attivi h24, 7 giorni su 7",
];

export default function ZonaLanding() {
  const { slug } = useParams();
  const zone = getZone(slug);

  const jsonLd = zone && {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: `${SITE.name} — ${zone.name}`,
    description: zone.intro,
    telephone: [SITE.phoneTel, SITE.phone2Tel],
    email: SITE.email,
    url: `${SITE_URL}/zone/${zone.slug}`,
    areaServed: [zone.name, ...zone.micro],
    address: { "@type": "PostalAddress", addressLocality: "Roma", addressRegion: "RM", addressCountry: "IT" },
    openingHours: "Mo-Su 00:00-23:59",
  };

  useSeo({
    title: zone
      ? `Rottamazione Auto ${zone.name} | Ritiro Gratuito H24 — Roma`
      : "Rottamazione Auto Roma",
    description: zone
      ? `Rottamazione auto ${zone.name} (Roma): ritiro gratuito a domicilio, demolizione certificata e radiazione al PRA. Chiama ${SITE.phoneDisplay} o scrivi su WhatsApp.`
      : "Rottamazione auto a Roma con ritiro gratuito.",
    path: zone ? `/zone/${zone.slug}` : "/zone",
    jsonLd,
  });

  if (!zone) return <Navigate to="/zone" replace />;

  return (
    <div>
      <section className="border-b border-[#232323] pt-40 pb-16">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8">
          <Link
            to="/zone"
            data-testid="back-to-zone"
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.16em] text-[#8a8a8a] transition-colors duration-150 hover:text-[#ff3300]"
          >
            <ArrowLeft size={14} /> Tutte le zone
          </Link>
          <p className="mt-8 flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-[#8a8a8a]">
            <MapPin size={14} className="text-[#ff3300]" /> {zone.tag} · Roma
          </p>
          <h1 className="mt-4 font-num uppercase leading-[0.85] tracking-tight text-[12vw] md:text-[7.5vw]">
            <MaskedLines lines={["Rottamazione Auto", zone.name]} />
          </h1>
          <p className="mt-8 max-w-2xl font-mono text-sm leading-relaxed text-[#8a8a8a]">
            {zone.intro}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <ContactMenu
              type="call"
              label="Chiama Ora"
              testid="zona-call"
              className="flex items-center gap-3 border border-[#ff3300] bg-[#ff3300] px-6 py-4 font-mono text-xs uppercase tracking-[0.16em] text-black transition-colors duration-150 hover:bg-transparent hover:text-[#ff3300]"
            />
            <ContactMenu
              type="whatsapp"
              label="WhatsApp"
              testid="zona-whatsapp"
              className="flex items-center gap-3 border border-[#232323] px-6 py-4 font-mono text-xs uppercase tracking-[0.16em] transition-colors duration-150 hover:border-[#25d366] hover:text-[#25d366]"
            />
          </div>
        </div>
      </section>

      <section className="mx-auto grid max-w-[1400px] grid-cols-1 gap-12 px-5 md:px-8 py-20 md:py-28 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <Reveal>
            <h2 className="font-display text-4xl md:text-5xl tracking-wide">
              Ritiro e demolizione a {zone.name}
            </h2>
            <p className="mt-6 font-mono text-sm leading-relaxed text-[#8a8a8a]">
              {zone.body}
            </p>
            <p className="mt-4 font-mono text-sm leading-relaxed text-[#8a8a8a]">
              Contattaci al {SITE.phoneDisplay} oppure al {SITE.phone2Display}: fissiamo
              insieme il ritiro e in 24-48 ore liberiamo lo spazio dal tuo
              vecchio veicolo, senza spese e senza pratiche a tuo carico.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-10">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#8a8a8a]">
                / Zone servite in {zone.name}
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                {zone.micro.map((m) => (
                  <span
                    key={m}
                    className="border border-[#232323] px-4 py-2 font-mono text-xs uppercase tracking-[0.08em] text-[#8a8a8a]"
                  >
                    {m}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-5">
          <Reveal delay={0.15}>
            <div className="border border-[#232323] bg-[#0f0f0f] p-8">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#8a8a8a]">
                / Cosa include
              </p>
              <ul className="mt-6 space-y-4">
                {benefits.map((b) => (
                  <li key={b} className="flex items-start gap-3">
                    <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-[#ff3300]" />
                    <span className="font-mono text-sm text-[#f4f4f0]">{b}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 border-t border-[#232323] pt-6">
                <a
                  href={`tel:${SITE.phoneTel}`}
                  className="block font-display text-3xl tracking-wide transition-colors duration-150 hover:text-[#ff3300]"
                >
                  {SITE.phoneDisplay}
                </a>
                <a
                  href={`tel:${SITE.phone2Tel}`}
                  className="mt-1 block font-display text-3xl tracking-wide transition-colors duration-150 hover:text-[#ff3300]"
                >
                  {SITE.phone2Display}
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <Marquee items={[`Rottamazione ${zone.name}`, "Ritiro Gratuito", "Demolizione H24", "Certificato PRA"]} />

      <section className="mx-auto max-w-[1400px] px-5 md:px-8 py-20 md:py-28">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#8a8a8a]">
          / Altre zone di Roma
        </p>
        <div className="mt-8 grid grid-cols-1 gap-px border border-[#232323] bg-[#232323] sm:grid-cols-2 lg:grid-cols-3">
          {ZONES.filter((z) => z.slug !== zone.slug).map((z) => (
            <Link
              key={z.slug}
              to={`/zone/${z.slug}`}
              data-testid={`related-zone-${z.slug}`}
              className="group flex items-center justify-between bg-[#0f0f0f] p-6 transition-colors duration-150 hover:bg-[#1a1a1a]"
            >
              <span className="font-display text-2xl tracking-wide group-hover:text-[#ff3300]">
                {z.name}
              </span>
              <MapPin size={16} className="text-[#8a8a8a]" />
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
